import { Fragment, useState, useCallback, useMemo, memo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ButtonRounded, Caption, H3, Card, CardBlock } from '@comeon/ui';
import styled from 'styled-components';
import Icon, { ICON_LEFT, ICON_RIGHT } from '~/components/icon/index.js';
import {
	WE_SPIN_CLIP_CARD_DATA_AT,
	WE_SPIN_CLIP_CARD_PRIMARY_BTN_DATA_AT
} from './constants/we-spin-data-at-attributes.js';
import WeSpinClipMedia from './we-spin-clip-media';
import WeSpinClipAddClipModal from './we-spin-clip-add-clip-modal.js';
import { selectIsStreamer } from './ducks/we-spin-dashboard-duck.js';
import breakpoint from '~/utils/style-utils.ts';
import { createClip } from './ducks/we-spin-duck.js';
import { ArrowLeft, ArrowRight } from '../../slider/SliderComponents.js';

const DarkCardBlock = styled(CardBlock)`
	background: var(--we-spin-clip-card-bg, var(--card-bg));
	position: relative;
`;

const PositionedFigure = styled.figure`
	height: 348px;
	order: 1;
	h3 {
		color: var(--we-spin-clip-card-title);
	}
	small {
		color: rgba(var(--font-supportive), 1);
	}
	${breakpoint.lg`
		order: 998;
	`}
`;

const Left = styled(ArrowLeft)`
	.icon--sprite {
		transform: translate(-9px, -2px);
	}
`;
const Right = styled(ArrowRight)`
	.icon--sprite {
		transform: translate(-1px, -3px);
	}
`;

const WeSpinClipMediaWrapper = styled.div`
	display: flex;
	flex-direction: column;
	position: relative;
	height: 100%;
	width: 100%;
	flex: 1;
`;

const WeSpinClipCard = ({ clips, translations }) => {
	const dispatch = useDispatch();

	const [addClipModalOpen, setAddClipModalOpen] = useState(false);

	const [currentClipIndex, setCurrentClipIndex] = useState(0);

	const openAddClipModal = useCallback(() => {
		setAddClipModalOpen(true);
	}, [setAddClipModalOpen]);

	const closeAddClipModal = useCallback(() => {
		setAddClipModalOpen(false);
	}, [setAddClipModalOpen]);

	const isStreamer = useSelector(selectIsStreamer);

	const saveClipCallback = useCallback(
		async ({ url }) => {
			await dispatch(
				createClip({
					url
				})
			);
		},
		[dispatch]
	);

	const currentClip = useMemo(() => {
		return clips[currentClipIndex];
	}, [clips, currentClipIndex]);

	const hasNextClip = useMemo(() => {
		return !!clips[currentClipIndex + 1];
	}, [clips, currentClipIndex]);

	const hasPreviousClip = useMemo(() => {
		return !!clips[currentClipIndex - 1];
	}, [clips, currentClipIndex]);

	const onNextClipCallback = useCallback(() => {
		if (hasNextClip) {
			setCurrentClipIndex(currentClipIndex + 1);
		}
	}, [hasNextClip, currentClipIndex]);

	const onPreviousClipCallback = useCallback(() => {
		if (hasPreviousClip) {
			setCurrentClipIndex(currentClipIndex - 1);
		}
	}, [hasPreviousClip, currentClipIndex]);

	return (
		<PositionedFigure
			className="u-relative u-m0 item--large-we-spin u-pb1 we-spin-clip-card"
			data-at={WE_SPIN_CLIP_CARD_DATA_AT}
		>
			<Card>
				<DarkCardBlock>
					<div className="u-text-c u-width-100 u-center-vertically u-align-vert">
						<Caption className="u-mt1">{translations.WE_SPIN_CLIP_CARD_MAIN_CAPTION}</Caption>
						{isStreamer && (
							<Fragment>
								<H3 mt={2} mb={3} className="u-text-c">
									{translations.WE_SPIN_CLIP_CARD_STREAMER_HEADER}
								</H3>
								<ButtonRounded
									className="btn btn--light btn--small btn--clip-card"
									onClick={openAddClipModal}
									dataAt={WE_SPIN_CLIP_CARD_PRIMARY_BTN_DATA_AT}
								>
									{translations.WE_SPIN_CLIP_CARD_ADD_BUTTON}
								</ButtonRounded>
								<Caption className="u-pt1-2">
									{translations.WE_SPIN_CLIP_CARD_TIME_FOR_REVIEW_CAPTION}
								</Caption>
							</Fragment>
						)}
						{!isStreamer && (
							<H3 mt={2} mb={3} className="u-text-c">
								{translations.WE_SPIN_CLIP_CARD_NOT_STREAMER_HEADER}
							</H3>
						)}
						<Left show={hasPreviousClip} onClick={onPreviousClipCallback}>
							<Icon type={ICON_LEFT} />
						</Left>
						<Right show={hasNextClip} onClick={onNextClipCallback}>
							<Icon type={ICON_RIGHT} />
						</Right>
					</div>
				</DarkCardBlock>
				{currentClip && (
					<WeSpinClipMediaWrapper>
						<WeSpinClipMedia clip={currentClip} />
					</WeSpinClipMediaWrapper>
				)}
			</Card>
			<WeSpinClipAddClipModal
				openModal={addClipModalOpen}
				hideModalCallback={closeAddClipModal}
				saveClipCallback={saveClipCallback}
				translations={translations}
			/>
		</PositionedFigure>
	);
};

const propsEqual = (prevProps, nextProps) => {
	return (
		JSON.stringify(prevProps.clips) === JSON.stringify(nextProps.clips) &&
		JSON.stringify(prevProps.translations) === JSON.stringify(nextProps.translations)
	);
};
export default memo(WeSpinClipCard, propsEqual);
